import type { ReviewFormData } from '../../model/review/interfaces';

type ReviewFormState = ReviewFormData;

// Validation rules
export const VALIDATION_RULES = {
  NICKNAME_MIN_LENGTH: 4,
  NICKNAME_MAX_LENGTH: 25,
  TITLE_MAX_LENGTH: 200,
  CONTENT_MAX_LENGTH: 5000,
  MAX_IMAGES: 5,
} as const;

// Validation functions
export const isValidNickname = (nickname: string): boolean => {
  const trimmed = nickname.trim();
  return (
    trimmed.length >= VALIDATION_RULES.NICKNAME_MIN_LENGTH && trimmed.length <= VALIDATION_RULES.NICKNAME_MAX_LENGTH
  );
};

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return email.trim().length > 0 && emailRegex.test(email.trim());
};

export const isValidRating = (rating: number): boolean => {
  return rating > 0 && rating <= 5;
};

export const isValidTitle = (title: string): boolean => {
  return title.trim().length > 0 && title.length <= VALIDATION_RULES.TITLE_MAX_LENGTH;
};

export const isValidContent = (content: string): boolean => {
  return content.trim().length > 0 && content.length <= VALIDATION_RULES.CONTENT_MAX_LENGTH;
};

export const isValidImageCount = (images: File[]): boolean => {
  return images.length <= VALIDATION_RULES.MAX_IMAGES;
};

// Combined validation for form submission
// Members submit with their account name/email, so nickname/email inputs are hidden and
// must not block submission (e.g. an account display name shorter than the guest minimum).
export const isFormValid = (formState: ReviewFormState, isMember: boolean = false): boolean => {
  const isIdentityValid = isMember || (isValidNickname(formState.nickname) && isValidEmail(formState.email));

  return (
    isValidRating(formState.rating) &&
    isValidTitle(formState.title) &&
    isValidContent(formState.content) &&
    isIdentityValid &&
    isValidImageCount(formState.images)
  );
};

// Get validation errors
export interface ValidationErrors {
  rating?: string;
  title?: string;
  content?: string;
  nickname?: string;
  email?: string;
  images?: string;
}

export const getValidationErrors = (formState: ReviewFormState): ValidationErrors => {
  const errors: ValidationErrors = {};

  if (!isValidRating(formState.rating)) {
    errors.rating = 'Please select a rating';
  }

  if (!isValidTitle(formState.title)) {
    if (formState.title.trim().length === 0) {
      errors.title = 'Title is required';
    } else {
      errors.title = `Title must be ${VALIDATION_RULES.TITLE_MAX_LENGTH} characters or less`;
    }
  }

  if (!isValidContent(formState.content)) {
    if (formState.content.trim().length === 0) {
      errors.content = 'Review content is required';
    } else {
      errors.content = `Review must be ${VALIDATION_RULES.CONTENT_MAX_LENGTH} characters or less`;
    }
  }

  if (!isValidNickname(formState.nickname)) {
    if (formState.nickname.trim().length === 0) {
      errors.nickname = 'Nickname is required';
    } else if (formState.nickname.trim().length < VALIDATION_RULES.NICKNAME_MIN_LENGTH) {
      errors.nickname = `Nickname must be at least ${VALIDATION_RULES.NICKNAME_MIN_LENGTH} characters`;
    } else {
      errors.nickname = `Nickname must be ${VALIDATION_RULES.NICKNAME_MAX_LENGTH} characters or less`;
    }
  }

  if (!isValidEmail(formState.email)) {
    if (formState.email.trim().length === 0) {
      errors.email = 'Email is required';
    } else {
      errors.email = 'Please enter a valid email address';
    }
  }

  if (!isValidImageCount(formState.images)) {
    errors.images = `Maximum ${VALIDATION_RULES.MAX_IMAGES} images allowed`;
  }

  return errors;
};
